/**
 * Romanian copy — the reference dictionary.
 *
 * This file is the source of truth for the `Dictionary` shape. A second locale
 * is added by copying this file, translating the values, and registering it in
 * `./index.ts`. TypeScript will then flag any key the new locale forgets.
 *
 * Rules for adding copy here:
 *  - No string concatenation at the call site. If a phrase needs a value
 *    interpolated, expose it as a function. Word order differs between
 *    languages; concatenation bakes in Romanian word order.
 *  - No copy in components. If you are about to type a user-facing string in a
 *    .tsx file, it belongs here instead.
 *
 * Voice — carried over verbatim from the land tool. The audience is identical in
 * psychology: the PASSIVE owner who inherited the apartment or has it sitting
 * rented or empty, is sceptical, and is bombarded by "cumpărăm apartamente" spam.
 *  - Formal address ("dumneavoastră") throughout.
 *  - Register split on purpose: the impersonal reflexive for PROCESS and the
 *    first person plural for RELATIONSHIP. A firm states what happens; people
 *    answer for it.
 *  - NO EM DASH anywhere in user-facing copy. The en dash survives only inside
 *    numeric ranges ("3–5 zile", "1945–1963"). Where an em dash used to join
 *    clauses, the copy breaks the sentence instead.
 *  - No intention is required of the owner. Copy validates simply owning the
 *    apartment, never a sale in progress.
 *
 * The instrument is named in all copy: "scor de revalorificare imobiliară".
 */

export const ro = {
  meta: {
    htmlLang: 'ro',
    documentTitle: 'Scor de revalorificare imobiliară | PRQ Partners',
    metaDescription:
      'Scorul de revalorificare al imobilului dumneavoastră, pregătit individual de o echipă cu expertiză arhitecturală. Estimarea valorii actuale și diferența pe care ar aduce-o o renovare. Fără listare, fără oferte nesolicitate.',
  },

  brand: {
    name: 'PRQ Partners',
    wordmark: 'PRQ',
    tagline: 'Analiză de revalorificare imobiliară',
  },

  app: {
    /*
     * The hero, built like the one on prqpartners.com: a display claim, one
     * paragraph under it, then a single glass call to action carrying its own
     * qualifying line. Copy is v1 and will be iterated.
     */
    title: 'Valoarea unui imobil vechi stă în ce poate deveni.',
    intro:
      'Poate l-ați moștenit, poate stă închiriat sau gol de ani de zile. Nu îl puteți renova și nu vreți să îl dați pe nimic. Primul pas este să știți ce dețineți.',
    /*
     * The arrow is NOT part of this string — it is a decorative span in the
     * markup, so a screen reader announces the action and not "right arrow".
     */
    ctaLabel: 'Aflați valoarea imobilului',
    /* The line under the button, not inside it. Same italic grey treatment as a
       field's help line. "analiza" is feminine: "gratuită", "trimisă". */
    ctaNote: 'Analiza gratuită, trimisă prin email.',
    stepOf: (current: number, total: number) => `Pasul ${current} din ${total}`,
  },

  /**
   * The assurance block: answers to the sceptic's objections, rendered as a FAQ
   * accordion copying the formatting of the FAQ section on prqpartners.com.
   * Every heading is a question and ends in a question mark.
   */
  assurance: {
    heading: 'Întrebări frecvente',
    items: [
      {
        heading: 'Cine face analiza?',
        body: 'PRQ Partners este un studio de consultanță strategică pentru dezvoltări imobiliare. Fiecare analiză este pregătită individual, de o echipă cu expertiză arhitecturală.',
      },
      {
        /*
         * States the business model plainly: the analysis is free because the
         * market understanding it builds is what PRQ sells. An owner who works
         * that out after consenting feels sold; one told before is levelled with.
         */
        heading: 'De ce este gratuită?',
        body: 'Analizele de imobile sunt modul în care ne construim înțelegerea pieței, imobil cu imobil. Această înțelegere este valoarea muncii noastre în relația cu partenerii cu care lucrăm. De la dumneavoastră nu cerem nimic.',
      },
      {
        heading: 'Ce primesc, mai exact?',
        body: 'Scorul de revalorificare al imobilului, de la 0 la 100, calculat pe cinci criterii. Alături de scor, o estimare a valorii actuale și diferența de valoare pe care ar aduce-o o renovare corectă. La final, un răspuns direct: merită sau nu.',
      },
      {
        /* Photos are recommended, not required. Says so plainly, so an owner
           without good photos does not abandon the form. */
        heading: 'Trebuie neapărat să trimit fotografii?',
        body: 'Nu. Fotografiile nu sunt obligatorii, dar ajută mult: pe baza lor evaluarea este mult mai exactă. Nu trebuie să arate bine, ci exact cum este imobilul acum. Puteți trimite solicitarea și fără ele.',
      },
      {
        heading: 'Imobilul este într-o clădire cu bulină roșie. Are rost?',
        body: 'Da. Încadrarea seismică este unul dintre criteriile analizei, nu o condiție de acces. O bulină roșie schimbă calculul, iar în document veți vedea limpede cum și ce ar însemna o consolidare.',
      },
      {
        heading: 'Imobilul este în coproprietate sau succesiunea nu este finalizată. Pot trimite?',
        body: 'Da. Situația juridică este unul dintre cele cinci criterii, nu o condiție de acces. O succesiune nefinalizată se reflectă în punctaj, iar în document veți vedea limpede ce ar schimba finalizarea ei.',
      },
      {
        heading: 'Cât de exactă este estimarea de valoare?',
        body: 'Estimarea este un interval orientativ, ancorat pe grilele notariale și pe nivelurile actuale ale pieței, cu logica de calcul prezentată în document. Nu este un raport de evaluare în sensul standardelor ANEVAR și nu poate înlocui unul acolo unde legea îl cere.',
      },
      {
        /* The developer-interest FAQ pattern, framed as investment interest. */
        heading: 'Ce se întâmplă dacă apare interes de investiție în imobilul meu?',
        body: 'Vă scriem și vă întrebăm dacă doriți o discuție. Dumneavoastră decideți. Fără acordul dumneavoastră, imobilul nu este prezentat nimănui, iar datele lui nu părăsesc PRQ.',
      },
      {
        heading: 'Ce se întâmplă cu datele și fotografiile mele?',
        body: 'Sunt folosite pentru analiza imobilului și pentru a vă contacta, inclusiv atunci când există interes de investiție sau revalorificare pentru imobilul dumneavoastră. Sunt stocate pe infrastructura noastră și sunt șterse după maximum 24 de luni sau oricând ne cereți. Nu sunt transmise nimănui fără acordul dumneavoastră. Detaliile complete sunt în politica de confidențialitate.',
      },
    ],
  },

  steps: {
    apartment: 'Imobilul',
    condition: 'Starea și fotografiile',
    contact: 'Contact',
  },

  nav: {
    back: 'Înapoi',
    continue: 'Continuați',
    submit: 'Trimiteți solicitarea',
  },

  step1: {
    heading: 'Imobilul',
    lede: 'Spuneți-ne unde este imobilul și câteva date despre clădire. Cu cât oferiți mai multe detalii, cu atât evaluarea este mai exactă.',
    /**
     * The soft score gate: shown as a dialog when "Continuați" is pressed with
     * the score-critical fields still empty. It informs, it never blocks.
     */
    scoreGate: {
      title: 'Continuați fără scor?',
      intro: (fields: string) =>
        `Nu ați completat ${fields}. Fără aceste date primiți estimarea de valoare, dar scorul de revalorificare rămâne indisponibil.`,
      note: 'La pasul următor, starea imobilului contează și ea pentru scor.',
      missingAn: 'anul construcției',
      missingSeismic: 'încadrarea seismică',
      stay: 'Completez datele',
      skip: 'Continui fără scor',
    },

    addressLabel: 'Adresa imobilului',
    addressPlaceholder: 'ex. Strada Ion Câmpineanu 11, București',
    addressHint: 'Căutarea este limitată la București. Tastați strada și numărul, apoi alegeți adresa din sugestii.',
    addressSearching: 'Se caută…',
    addressNoResults: 'Niciun rezultat. Verificați scrierea sau căutați doar numele străzii.',
    addressError: 'Căutarea nu a funcționat. Verificați conexiunea sau scrieți adresa manual.',
    addressClear: 'Ștergeți adresa',
    addressManualNote: 'Adresă introdusă manual.',

    anConstructieLabel: 'An construcție',
    anConstructiePlaceholder: 'Alegeți perioada',
    anConstructieOptions: {
      pre1945: 'Înainte de 1945',
      y1945_1963: '1945–1963',
      y1964_1977: '1964–1977',
      y1978_1989: '1978–1989',
      post1990: '1990–2009',
      post2010: '2010 sau mai nou (bloc nou)',
      nustiu: 'Nu știu',
    },

    tipImobilLabel: 'Tip imobil',
    tipImobilPlaceholder: 'Alegeți tipul',
    tipImobilOptions: {
      apartament: 'Apartament',
      casa: 'Casă / vilă',
      altul: 'Altul',
    },

    etajLabel: 'Etaj',
    etajApartamentLabel: 'Etajul',
    etajApartamentPlaceholder: 'ex. 3',
    etajeTotalLabel: 'Din câte etaje',
    etajeTotalPlaceholder: 'ex. 8',
    etajHelp: 'Etajul și câte etaje are clădirea, dacă este cazul. Parter se poate scrie ca „P”.',

    regimLabel: 'Regim de înălțime',
    regimPlaceholder: 'ex. P+1',
    regimHelp: 'Numărul de niveluri ale clădirii (ex. P, P+1, P+2).',

    suprafataLabel: 'Suprafață utilă (m²)',
    suprafataPlaceholder: 'ex. 62',
    suprafataHelp: 'Suprafața utilă declarată, în metri pătrați.',

    /* House-only: the plot the building sits on. */
    suprafataTerenLabel: 'Suprafață teren (m²)',
    suprafataTerenPlaceholder: 'ex. 250',
    suprafataTerenHelp: 'Suprafața terenului pe care se află imobilul, în metri pătrați.',

    camereLabel: 'Număr camere',
    camerePlaceholder: 'Alegeți',
    camereOptions: {
      '1': '1 cameră',
      '2': '2 camere',
      '3': '3 camere',
      '4': '4 camere',
      '5plus': '5 camere sau mai multe',
    },

    seismicLabel: 'Încadrare seismică',
    /* Verbatim from the brief. */
    seismicHelp:
      'Se poate verifica după adresă în listele publice ale Primăriei. Dacă nu știți, verificăm noi.',
    seismicPlaceholder: 'Alegeți încadrarea',
    seismicOptions: {
      rsi: 'Clasa RsI (bulină roșie)',
      rsii: 'Clasa RsII',
      rsiii: 'Clasa RsIII',
      urgente: 'Categorie de urgență (U1–U3)',
      fara: 'Fără încadrare',
      consolidat: 'Consolidat',
      nustiu: 'Nu știu',
    },

    /* Apartment-only fields (shown when Tip imobil is Apartament or unset). */
    compartimentareLabel: 'Compartimentare',
    compartimentarePlaceholder: 'Alegeți compartimentarea',
    compartimentareOptions: {
      decomandat: 'Decomandat',
      semidecomandat: 'Semidecomandat',
      nedecomandat: 'Nedecomandat',
      circular: 'Circular',
      open_space: 'Open space',
    },

    balconLabel: 'Balcon',
    balconPlaceholder: 'Alegeți',
    balconOptions: {
      nu: 'Fără balcon',
      deschis: 'Balcon deschis',
      inchis: 'Balcon închis',
    },

    /* Shown only when the balcony is open/closed, so we can use its real area. */
    suprafataBalconLabel: 'Suprafață balcon (m²)',
    suprafataBalconPlaceholder: 'ex. 4',
    suprafataBalconHelp: 'Suprafața balconului, în metri pătrați.',

    /* Inline checkbox next to the floor. */
    liftLabel: 'Lift',

    sursaIncalzireLabel: 'Sursă încălzire',
    sursaIncalzirePlaceholder: 'Alegeți sursa',
    sursaIncalzireOptions: {
      centrala_proprie: 'Centrală proprie',
      termoenergetica: 'Termoenergetica',
      centrala_bloc: 'Centrală de bloc',
      calorifere_electrice: 'Calorifere electrice',
    },

    /* House-only fields (shown when Tip imobil is Casă / vilă or Altul). */
    structuraLabel: 'Structură',
    structuraPlaceholder: 'Alegeți structura',
    structuraOptions: {
      cadre_beton_armat: 'Cadre din beton armat',
      zidarie_portanta: 'Zidărie portantă',
      lemn: 'Structură din lemn',
      metalica: 'Structură metalică',
      chirpici_paianta: 'Chirpici / Paiantă / Pământ',
    },

    incalzireLabel: 'Încălzire',
    incalzirePlaceholder: 'Alegeți încălzirea',
    incalzireOptions: {
      centrala_gaz: 'Centrală pe gaz',
      lemne: 'Lemne',
      pompa_caldura: 'Pompă de căldură',
      fara: 'Fără',
    },

    gateAddress: 'Introduceți adresa imobilului pentru a continua.',
    gateSuprafata: 'Introduceți suprafața utilă pentru a continua.',
  },

  step2: {
    heading: 'Starea și fotografiile',
    /* Same "optional first" lede pattern as the land tool, adapted: more info
       means a more accurate evaluation. */
    lede: 'Toate câmpurile sunt opționale. Completați doar ce știți. Cu cât ne dați mai multe informații și fotografii, cu atât evaluarea este mai exactă.',

    stareLabel: 'Stare actuală',
    starePlaceholder: 'Alegeți starea',
    stareOptions: {
      renovat_recent: 'Renovat recent',
      renovat_invechit: 'Renovat, dar învechit',
      locuibil_nerenovat: 'Locuibil, nerenovat',
      necesita_renovare_completa: 'Necesită renovare completă',
    },

    /* Photos are the centerpiece. Label generalised from the brief's "Fotografii
       din apartament" so it fits houses and interbelic buildings too. */
    photosLabel: 'Fotografii din interior',
    photosHelp: 'Camerele principale, baia, bucătăria. Nu trebuie să arate bine. Exact așa cum este acum.',
    photosMeta: (max: number, maxMb: number) => `JPG, PNG, HEIC sau WEBP. Maximum ${max} fotografii, ${maxMb} MB fiecare.`,
    photosCta: 'Adăugați fotografii',
    photosDrop: 'sau trageți fotografiile aici',
    photosProcessing: 'Se pregătesc fotografiile…',
    photoCount: (n: number, max: number) => `${n} din ${max} fotografii`,

    planLabel: 'Planul imobilului',
    planOptional: 'opțional',
    planHelp: 'Dacă îl aveți: releveu, schiță sau planul din acte. JPG, PNG, HEIC sau WEBP, maximum 20 MB.',
    planCta: 'Alegeți fișierul',
    planCtaReplace: 'Înlocuiți fișierul',
    planDrop: 'sau trageți fișierul aici',

    /*
     * Legal status. Reused verbatim from the land tool. The help line is the
     * whole point of asking: an owner mid-succession assumes it disqualifies
     * them and closes the tab. Saying plainly that nothing here blocks the
     * analysis is what keeps that owner in the form.
     */
    situatieLabel: 'Situația juridică a imobilului',
    situatieHelp: 'Nicio situație nu blochează analiza.',
    situatiePlaceholder: 'Alegeți situația',
    situatieOptions: {
      unic: 'Un singur proprietar, intabulat',
      coproprietari: 'Mai mulți coproprietari',
      succesiune: 'Succesiune în curs sau nefinalizată',
      sarcini: 'Există sarcini sau neclarități (ipotecă, litigiu, acte vechi)',
      nustiu: 'Nu știu exact',
    },

    noteLabel: 'Intenția dumneavoastră',
    noteHelp:
      'Spuneți-ne, dacă doriți, cum ați ajuns la acest imobil și la ce vă gândiți. Nicio intenție nu este necesară pentru analiză.',
    /* Adapted from the brief's placeholder, generalised past "apartamentul". */
    notePlaceholder:
      'ex. Am moștenit imobilul de la bunici. Nu îl pot renova și nu vreau să îl vând pe nimic.',

    fileRemove: 'Eliminați',
    fileRemoveNamed: (name: string) => `Eliminați fișierul ${name}`,
    photoAlt: (name: string) => `Previzualizare ${name}`,
    photoNoPreview: 'HEIC',

    /*
     * Shown on step 3 when fewer than the recommended number of photos are
     * attached. It must NOT read as an error: the submission is valid without
     * photos. It states what we do with more, and normalises sending fewer.
     */
    photosFewHeading: 'Ați atașat puține fotografii',
    photosNoneHeading: 'Nu ați atașat fotografii',
    photosFewBody:
      'Solicitarea poate fi trimisă și așa. Cu mai multe fotografii, din camerele principale, baie și bucătărie, evaluarea este mult mai exactă. Multe solicitări încep exact așa.',
    photosFewAction: 'Adaug fotografii',
  },

  step3: {
    heading: 'Date de contact',
    lede: 'Scorul de revalorificare este trimis pe adresa de email de mai jos.',

    nameLabel: 'Nume și prenume',
    namePlaceholder: 'ex. Ioana Popescu',

    emailLabel: 'Email',
    emailPlaceholder: 'ex. ioana.popescu@email.ro',

    phoneLabel: 'Telefon',
    phonePlaceholder: 'ex. 0722 123 456',
    /* Same phoneHelp as the land tool. */
    phoneHelp: 'Folosit doar dacă este nevoie de o lămurire despre imobil. Fără apeluri de vânzare.',

    /*
     * Consent text, verbatim from the brief. Names the photos and the address
     * explicitly and the renovation/revalorification purpose, so the ask-first
     * promise in the FAQ is lawful rather than a surprise.
     *
     * Keep this in step with privacy.sections → "Scopul prelucrării". Same
     * promise written twice; only one is legally binding if they disagree.
     */
    consentText:
      'Sunt de acord ca PRQ Partners să prelucreze datele furnizate (inclusiv fotografiile încărcate și adresa imobilului) în scopul analizei imobilului și al contactării mele, inclusiv în legătură cu posibilități de investiție sau revalorificare. Datele sunt păstrate maximum 24 de luni și nu sunt transmise terților fără acordul meu.',
    consentLink: 'Politică de confidențialitate',

    summaryHeading: 'Rezumatul solicitării',
    summaryAddress: 'Adresă',
    summarySuprafata: 'Suprafață',
    summaryAn: 'An construcție',
    summaryPhotos: 'Fotografii',
    summarySuprafataValue: (value: string) => `${value} m²`,
    summaryPhotosCount: (n: number) => (n === 1 ? '1 fotografie' : `${n} fotografii`),
    summaryPlanAttached: '1 plan atașat',
    /* Empty-value placeholder. A word, not a dash: the copy rules forbid the em
       dash and reserve the en dash for numeric ranges. */
    summaryEmpty: 'Nespecificat',
  },

  validation: {
    suprafataInvalid: 'Suprafața trebuie să fie un număr mai mare decât 0.',
    suprafataTooLarge: 'Suprafața pare prea mare. Verificați valoarea introdusă.',
    addressRequired: 'Introduceți adresa imobilului.',
    addressPickFromList: 'Alegeți adresa din lista de sugestii — tastați strada și selectați varianta potrivită.',
    photoType: 'Format neacceptat. Sunt acceptate doar JPG, PNG, HEIC și WEBP.',
    photoTooLarge: (maxMb: number) => `Fișierul depășește ${maxMb} MB.`,
    tooManyPhotos: (max: number) => `Puteți atașa maximum ${max} fotografii.`,
    nameRequired: 'Introduceți numele dumneavoastră.',
    emailRequired: 'Introduceți adresa de email.',
    emailInvalid: 'Adresa de email nu pare validă.',
    phoneRequired: 'Introduceți numărul de telefon.',
    phoneInvalid: 'Numărul de telefon nu pare valid.',
    consentRequired: 'Bifați acordul pentru a putea trimite solicitarea.',
    fieldError: 'Eroare:',
  },

  submit: {
    errorHeading: 'Solicitarea nu a putut fi trimisă',
    errorGeneric:
      'A apărut o problemă la trimitere. Datele dumneavoastră au rămas în formular. Puteți încerca din nou.',
    errorNetwork:
      'Serverul nu a putut fi contactat. Verificați conexiunea la internet și încercați din nou.',
    errorValidation:
      'Serverul a respins câteva câmpuri. Verificați datele introduse și încercați din nou.',
    errorRateLimit:
      'S-au trimis prea multe solicitări de la această adresă. Vă rugăm să încercați din nou peste câteva minute.',
    errorTooLarge:
      'Fotografiile atașate sunt prea mari. Eliminați câteva sau folosiți fișiere mai mici și încercați din nou.',
    retry: 'Încercați din nou',
  },

  success: {
    eyebrow: 'Solicitare înregistrată',
    /*
     * headline, body and signature MUST STAY IDENTICAL to their counterparts in
     * server/src/mail/templates.ts. The owner reads them on screen and then
     * again in their inbox; if the two drift, the email reads like a different
     * promise. Change both in one commit.
     *
     * No "thank you": PRQ gives the analysis away.
     */
    headline: 'Am primit informațiile despre imobil.',
    /* Adapted from the brief, generalised past "apartament". Keep in step with
       the owner-confirmation email in server/src/mail/templates.ts. */
    body: 'O echipă cu expertiză arhitecturală analizează imobilul pe baza fotografiilor și a datelor trimise. Punctajul de renovare ajunge prin email în maximum 5 zile lucrătoare. Imobilul nu este listat și nu este oferit nimănui. Analiza este gratuită.',
    signature: 'PRQ Partners',
    referenceLabel: 'Număr de referință',
    referenceHelp: 'Păstrați acest număr dacă doriți să ne scrieți despre solicitare.',
    emailedTo: (email: string) => `Confirmarea a fost trimisă la ${email}.`,

    /*
     * "Ce primiți" — the deliverable, verbatim from the brief. Read AFTER the
     * commitment, so it confirms what is coming rather than bargaining for it.
     */
    deliverableHeading: 'Ce primiți',
    // Ordinea urmează noul raport: valoare → scor → recomandare → verdict. Formulat
    // ca să fie valabil pentru AMBELE tipuri de raport (cel transparent și cel de
    // oportunitate) — de aceea recomandarea e „concretă", nu „diferența de valoare"
    // (cifra de marjă e ascunsă în raportul pentru imobilele cu potențial ridicat).
    deliverableItems: [
      'Estimarea valorii actuale a imobilului',
      'Scorul de revalorificare, de la 0 la 100, pe cinci criterii',
      'O recomandare concretă de revalorificare, potrivită pentru imobil',
      'Un răspuns direct: merită sau nu o revalorificare acum',
    ],
  },

  consent: {
    regionLabel: 'Setări cookie-uri',
    body: 'Folosim cookie-uri pentru a măsura traficul și eficiența campaniilor.',
    accept: 'Accept',
    decline: 'Refuz',
    link: 'Detalii',
  },

  privacy: {
    title: 'Politică de confidențialitate',
    back: 'Înapoi la formular',
    lastUpdatedLabel: 'Ultima actualizare',
    lastUpdated: 'iulie 2026',
    sections: [
      {
        heading: 'Operatorul datelor',
        body: [
          'Operatorul datelor cu caracter personal colectate prin acest formular este Prerequis SRL, cu sediul în Str. Doctor Joseph Lister nr. 55, 050542 București, România, operând sub marca PRQ Partners. Pentru orice întrebare privind prelucrarea datelor, ne puteți scrie la contact@prqpartners.com.',
        ],
      },
      {
        heading: 'Ce date colectăm',
        body: [
          'Colectăm datele pe care le introduceți în formular: numele, adresa de email și numărul de telefon; adresa imobilului și, dacă alegeți o sugestie de căutare, coordonatele acesteia; detaliile despre imobil (anul construcției, tipul, etajul, suprafața, numărul de camere, încadrarea seismică, starea actuală, situația juridică); notele pe care ni le transmiteți; și fotografiile și planul pe care le încărcați.',
          'Colectăm de asemenea data și ora trimiterii și pagina de la care ați ajuns la formular.',
          'Dacă bifați acordul opțional de măsurare, înregistrăm faptul, momentul și adresa IP de la care a fost dat.',
        ],
      },
      {
        heading: 'Scopul prelucrării',
        body: [
          /* Renovation-interest purpose clause. Keep in step with
             step3.consentText — same promise, two places. */
          'Folosim aceste date pentru a analiza imobilul descris, a pregăti documentația pe care v-o trimitem prin email și a vă contacta în legătură cu această solicitare. Vă putem contacta și atunci când există interes concret de investiție sau revalorificare pentru imobilul dumneavoastră, pentru a vă întreba dacă doriți o discuție; datele imobilului nu sunt prezentate niciunui terț fără acordul dumneavoastră expres, exprimat la acel moment. Nu folosim datele pentru profilare automată și nu luăm decizii automate pe baza lor.',
        ],
      },
      {
        heading: 'Pagina cu analiza imobilului',
        body: [
          'Analiza finalizată v-o punem la dispoziție și pe o pagină web dedicată, accesibilă printr-un link privat pe care vi-l trimitem pe email. Pagina cuprinde un rezumat al analizei și raportul complet în format PDF. Linkul este privat și nu este indexat: numai cine îl deține poate deschide pagina. Pagina și raportul sunt păstrate pe aceeași perioadă ca restul datelor solicitării.',
          'Pe această pagină vă putem cere, opțional, acordul de a fi contactat dacă apare un interes concret de investiție sau renovare pentru imobilul dumneavoastră. Bifarea este opțională și nu condiționează în niciun fel accesul la analiză sau la raport. Dacă vă dați acordul, înregistrăm faptul, momentul și adresa IP de la care a fost dat, ca dovadă a consimțământului; îl puteți retrage oricând de pe pagină.',
        ],
      },
      {
        heading: 'Temeiul legal',
        body: [
          'Prelucrarea se face pe baza consimțământului dumneavoastră, exprimat prin bifarea căsuței din formular, conform art. 6 alin. (1) lit. (a) din Regulamentul (UE) 2016/679 (GDPR). Vă puteți retrage consimțământul oricând, scriindu-ne la adresa de contact.',
        ],
      },
      {
        heading: 'Perioada de păstrare',
        body: [
          'Păstrăm datele maximum 24 de luni de la data trimiterii solicitării, după care le ștergem. Dacă ne cereți ștergerea mai devreme, o facem fără întârziere nejustificată.',
        ],
      },
      {
        heading: 'Servicii tehnice ale unor terți',
        body: [
          /* Nominatim only — this app has no map tiles, so there is no Mapbox
             disclosure. If a static location preview is added, disclose it here. */
          'Pentru căutarea adreselor folosim serviciul Nominatim (OpenStreetMap). Când folosiți căutarea, adresa IP a dispozitivului dumneavoastră și termenii căutați sunt transmise acestui serviciu, conform politicii lui de confidențialitate. Serviciul nu primește fotografiile sau celelalte date din formular.',
          /*
           * The Meta pixel. Separated on purpose: the address search is
           * functionally necessary and runs without being asked, this one is
           * marketing measurement and runs only on consent.
           */
          'Dacă v-ați exprimat acordul, încărcăm și instrumentul de măsurare al Meta Platforms Ireland Limited (Meta Pixel). Acesta transmite către Meta adresa IP a dispozitivului, informații despre browser și momentele în care deschideți formularul, completați datele imobilului și trimiteți solicitarea. Ne arată ce anunțuri aduc proprietari de imobile pe această pagină.',
          'Instrumentul înregistrează automat și alte interacțiuni cu pagina, cum sunt apăsările de butoane. Nu citește însă datele pe care le introduceți în formular: numele, adresa de email și numărul de telefon nu sunt transmise către Meta. Fără acordul dumneavoastră instrumentul nu se încarcă și nu transmite nimic, iar acordul poate fi retras oricând din subsolul paginii.',
        ],
      },
      {
        heading: 'Transmiterea către terți',
        body: [
          'Nu transmitem datele din formular către terți fără acordul dumneavoastră explicit. Datele și fotografiile sunt stocate pe infrastructura controlată de PRQ Partners.',
        ],
      },
      {
        heading: 'Drepturile dumneavoastră',
        body: [
          'Aveți dreptul de acces la date, de rectificare, de ștergere, de restricționare a prelucrării, de portabilitate și de opoziție. Aveți de asemenea dreptul de a depune o plângere la Autoritatea Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal (ANSPDCP).',
        ],
      },
      {
        heading: 'Contact',
        body: [
          'Pentru exercitarea drepturilor de mai sus sau pentru orice întrebare privind prelucrarea datelor, ne puteți scrie la contact@prqpartners.com.',
        ],
      },
    ],
  },

  footer: {
    privacy: 'Politică de confidențialitate',
    consentReset: 'Setări de măsurare',
    rights: (year: number) => `© ${year} PRQ Partners`,
  },

  a11y: {
    skipToContent: 'Săriți la conținut',
    stepperLabel: 'Etapele solicitării',
    stepComplete: 'finalizat',
    stepCurrent: 'pasul curent',
  },
} as const;

/** The shape every locale must satisfy. Derived from Romanian, the reference. */
export type Dictionary = typeof ro;
